// imports stay the same ...
import { useEffect, useState, useRef, useContext, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { mainContext } from "../../context/mainContext";
import { useTranslation } from "../../hooks/useTranslation";
import "../../assets/styles/servicesSelector.css";

const BREAKPOINT = 1400;          // matches CSS 1399.98

/* ---------- tiny helper ---------- */
const useIsMobile = () => {
  const [m, setM] = useState(window.innerWidth < BREAKPOINT);
  useEffect(() => {
    const handler = () => setM(window.innerWidth < BREAKPOINT);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return m;
};

export default function ServicesSelector({ hideDrawer }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { navHeight, setNavActive } = useContext(mainContext);
  const t = useTranslation();

  /* write custom property every time navHeight changes */
  useLayoutEffect(() => {
    document.documentElement
      .style.setProperty("--nav-height", `${navHeight}px`);
  }, [navHeight]);

  /* close on outside click / Esc */
  useEffect(() => {
    const close = (e) => {
      const target = e.target;

      if (
        target.closest('a') &&
        target.href
      ) {
        return;
      }

      if (ref.current && !ref.current.contains(target)) {
        setOpen(false);
      }

      if (e instanceof KeyboardEvent && e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", close);
    document.addEventListener("keyup", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keyup", close);
    };
  }, []);

  const smoothScroll = id => {
    document.getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSelect = id => {
    setOpen(false);
    hideDrawer?.();
    setNavActive("szolgaltatasok");

    if (pathname === "/szolgaltatasok") {
      window.history.replaceState(null, "", `#${id}`);  // update URL hash
      requestAnimationFrame(() => smoothScroll(id));  // smooth scroll
    } else {
      navigate(`/szolgaltatasok#${id}`);
      setTimeout(() => smoothScroll(id), 60);
    }
  };

  // const [showDrawer, setShowDrawer] = useState(false);
  const [showDrawer] = useState(false);
  useEffect(() => {
    if (isMobile && showDrawer) {
      const handleDropdownOpen = () => {
        if (open) {
          setTimeout(() => {
            const dropdown = document.querySelector('.servicesDropdown.isOpen');
            if (dropdown) {
              dropdown.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest'
              });
            }
          }, 100);
        }
      };

      handleDropdownOpen();
    }
  }, [open, isMobile, showDrawer]);

  /* ------------ render ------------ */
  return (
    /* this span lives inside the NavLink on desktop,
       or inside a normal <span> on mobile */
    <span
      ref={ref}
      className="servicesWrapper position-relative"
      /* hover only active on desktop */
      onMouseEnter={() => !isMobile && setOpen(true)}
      onMouseLeave={() => !isMobile && setOpen(false)}
      /* mobile: clicking text should only toggle, not navigate */
      onClick={e => {
        if (isMobile) {          // stop NavLink navigation
          e.preventDefault();
          e.stopPropagation();
          setOpen(o => !o);
        }
      }}
    >
      <div className="servicesLabelRow">
        <span className="servicesText">{t("nav.services")}</span>
        <button
          type="button"
          className="caretBtn"
          aria-expanded={open}
          aria-label={t("nav.openServices")}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setOpen(o => !o);
          }}
        >
          <ChevronDown size={14} className={open ? "rotate" : ""} />
        </button>
      </div>

      <div className={`servicesDropdown ${open ? "isOpen" : ""}`}>
        <div className="servicesDropdown-inner">
          {/* <select
          style={{
            zIndex: '-1',
            opacity: '0',
            width: '100%',
            position: 'absolute',
            top: '-1.5rem',
            height: '2.5rem',
            cursor: 'pointer',
            border: 'none',
            padding: '0',
            backgroundColor: 'transparent'
          }}
        ></select> */}

          {["pilates", "koredzes", "trx",
            "alakformalo", "mobilitas", "funkcionalis", "szemelyiedzes"].map(id => (
              <button
                key={id}
                className="servicesItem"
                onClick={e => {
                  e.preventDefault(); e.stopPropagation();
                  handleSelect(id);
                }}
              >
                {t(`nav.servicesDropdown.${id}`)}
              </button>
            ))}
        </div>
      </div>
    </span>
  );
}
